/**
 * Weight budget for background /info reads (#1809): a bucket that refills at `perMinute` and holds at most `burst`.
 * take() waits until the bucket can pay. charge() adds a cost only known from the answer (20 + rows/20 for fills) and may
 * leave the bucket in debt, which later take() calls wait out, so spending stays within burst + perMinute per minute
 * plus the last such charge.
 *
 * One pacer per InfoClient outlives pages and refreshes: starting over must not hand out a new budget. It covers one tab
 * and only the reads that use it, and a page reload starts with a full bucket.
 */
import { abortError, defaultSleep, type InfoClient } from './info';

/** Budget of all background reads, half of Hyperliquid's 1200/min per IP. What a user asks for directly is not counted. */
export const BACKGROUND_WEIGHT_PER_MIN = 600;
/** Spent at once from a full bucket: both 2000-row downloads of a wallet, with lookups waiting behind them. */
export const BACKGROUND_BURST_WEIGHT = 240;

export interface WeightPacerOptions {
  perMinute: number;
  burst: number;
  now?: () => number;
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
}

export interface TakeOptions {
  signal?: AbortSignal;
  /** Checked before paying and after each wait. False ends the wait with `false` and nothing is spent. */
  keepGoing?: () => boolean;
}

export class WeightPacer {
  private readonly perMs: number;
  private readonly burst: number;
  private readonly now: () => number;
  private readonly sleep: (ms: number, signal: AbortSignal) => Promise<void>;
  private level: number;
  private at: number;

  constructor(opts: WeightPacerOptions) {
    this.perMs = opts.perMinute / 60_000;
    this.burst = opts.burst;
    this.now = opts.now ?? Date.now;
    this.sleep = opts.sleep ?? defaultSleep;
    this.level = opts.burst;
    this.at = this.now();
  }

  private refill(): void {
    const t = this.now();
    this.level = Math.min(this.burst, this.level + Math.max(0, t - this.at) * this.perMs);
    this.at = t;
  }

  /** Resolves true once `weight` is paid. Rejects with an AbortError when `signal` aborts. */
  async take(weight: number, opts: TakeOptions = {}): Promise<boolean> {
    const signal = opts.signal ?? new AbortController().signal;
    // Never more than the bucket holds, or the wait would not end.
    const need = Math.min(weight, this.burst);
    for (;;) {
      if (signal.aborted) throw abortError();
      if (opts.keepGoing && !opts.keepGoing()) return false;
      this.refill();
      if (this.level >= need) {
        this.level -= need;
        return true;
      }
      await this.sleep(Math.ceil((need - this.level) / this.perMs), signal);
    }
  }

  charge(weight: number): void {
    this.refill();
    this.level -= weight;
  }
}

const pacers = new WeakMap<InfoClient, WeightPacer>();

/** The one pacer of a client (#1809): a new page, refresh or session must not start a new budget. */
export function backgroundPacer(info: InfoClient): WeightPacer {
  let pacer = pacers.get(info);
  if (!pacer) {
    pacer = new WeightPacer({ perMinute: BACKGROUND_WEIGHT_PER_MIN, burst: BACKGROUND_BURST_WEIGHT });
    pacers.set(info, pacer);
  }
  return pacer;
}
